import { useEffect, useRef, useState } from 'react';
import { Box, Stack, SxProps, Typography } from '@mui/material';
import * as Yup from 'yup';
import { useAuth } from 'services/auth/hooks';
import { useToast, ToastType } from 'services/toast';
import { getSearchParamsFroUrl } from 'utils/fomatting';
import { AuthLayout } from 'services/auth/components/AuthLayout';
import { Paths } from 'services/navigation/paths';
import { Error as ErrorIcon } from '@mui/icons-material';
import { Form, Fields, FormRef, emailSchema } from 'common/form';
import { RouterLink } from 'common/ui/RouterLink';
import { getInviteErrorMessageFromCognitoError } from 'services/auth/utils/getInviteErrorMessageFromCognitoError';
import { shouldRedirectToLoginPage } from 'services/auth/utils/shouldRedirectToLoginPage';
import { useNavigate } from 'react-router-dom';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  padding: 4,
  paddingTop: '10%'
};

const errorContainer: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  gap: 1,
  alignItems: 'center'
};

const inviteSignInSchema = Yup.object({
  email: emailSchema
});
type SignInFormData = Yup.InferType<typeof inviteSignInSchema>;

const signInFields: Fields<SignInFormData> = {
  email: {
    label: 'Email',
    type: 'text',
    subLabel: (
      <Stack direction="row" alignItems="center" gap={1}>
        <RouterLink to={Paths.LOGIN}>Log in here</RouterLink>
        <Typography>if you already have an account.</Typography>
      </Stack>
    )
  }
};

const InviteTempSignInForm: React.FC<{ onValidation: (user: any) => void }> = ({
  onValidation
}) => {
  const formRef = useRef<FormRef<SignInFormData>>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { username: emailParam, tp: tempPasswordParam } = getSearchParamsFroUrl(
    window.location.href
  );
  const { signInWithTemPassword } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const handleFormSubmit = async (data: SignInFormData) => {
    try {
      if (!tempPasswordParam) {
        showToast({
          message: 'Invalid invitation link',
          type: ToastType.Error
        });
        return;
      }

      setIsLoading(true);
      const user = await signInWithTemPassword(data.email, tempPasswordParam);
      setIsLoading(false);
      onValidation(user);
    } catch (e: any) {
      if (shouldRedirectToLoginPage(e?.message)) {
        navigate(`${Paths.LOGIN}?email=${data.email}&source=invite`);
        return;
      }

      showToast({
        message: getInviteErrorMessageFromCognitoError(e?.message),
        type: ToastType.Error
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (emailParam) {
      formRef?.current?.setValue('email', emailParam);
    }
  }, [emailParam]);

  if (!tempPasswordParam) {
    return (
      <Box sx={errorContainer}>
        <ErrorIcon fontSize="small" color="error" />
        <Typography>Invalid invitation link.</Typography>
      </Box>
    );
  }

  return (
    <AuthLayout title="Get started" isSubmitting={isLoading}>
      <Form
        ref={formRef}
        schema={inviteSignInSchema}
        fields={signInFields}
        onSubmit={handleFormSubmit}
        submitButtonProps={{
          label: isLoading ? 'Loading...' : 'Continue',
          disabled: isLoading,
          fullWidth: true,
          sx: {
            py: '16px'
          }
        }}
      />
    </AuthLayout>
  );
};

const inviteResetPasswordSchema = Yup.object({
  password: Yup.string()
    .min(8, 'Must be at least 8 characters')
    .required('Password is required'),
  passwordConfirmation: Yup.string()
    .required('Password confirmation is required')
    // @ts-expect-error : TS2322
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
});
type ResetPasswordFormData = Yup.InferType<typeof inviteResetPasswordSchema>;

const resetPasswordFields: Fields<ResetPasswordFormData> = {
  password: {
    label: 'Password',
    type: 'password'
  },
  passwordConfirmation: {
    label: 'Confirm password',
    type: 'password'
  }
};

const InviteSetPasswordForm: React.FC<{ user: any }> = ({ user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { completeNewPassword } = useAuth();
  const { showToast } = useToast();

  const handleFormSubmit = async (data: ResetPasswordFormData) => {
    try {
      setIsLoading(true);
      await completeNewPassword(user, data.password);
      setIsLoading(false);
    } catch (e: any) {
      showToast({
        message: 'We could not reset your password',
        type: ToastType.Error
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout title="Sign up your account" isSubmitting={isLoading}>
      <Form
        schema={inviteResetPasswordSchema}
        fields={resetPasswordFields}
        onSubmit={handleFormSubmit}
        submitButtonProps={{
          label: isLoading ? 'Loading...' : 'Continue',
          disabled: isLoading,
          fullWidth: true,
          sx: {
            py: '16px'
          }
        }}
      />
    </AuthLayout>
  );
};

const InvitePage: React.FC = () => {
  const [validatedUser, setValidatedUser] = useState(null);

  return (
    <Box sx={container}>
      {!validatedUser && (
        <InviteTempSignInForm onValidation={setValidatedUser} />
      )}
      {validatedUser && <InviteSetPasswordForm user={validatedUser} />}
    </Box>
  );
};

export { InvitePage };
